import Input from "../Input";
import { useEffect, useState } from "react";
import { DatePickerProps } from ".";
import {
  Popper,
  Paper,
  IconButton,
  Grid,
  Typography,
  ClickAwayListener,
  Box,
  Drawer,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useTranslation } from "react-i18next";
import CloseIcon from "assets/icons/close.svg";

// Helper: get days for a month
const getDaysInMonth = (month: number, year: number) => {
  const date = new Date(year, month, 1);
  const days = [];
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
};

export default function CustomDatePicker({
  onChange,
  editable,
  StartIcon,
  InputFieldProps,
  customLabelStyles = true,
  labelShrink,
  renderPickerOnly = false,
  disableInternalOpen = false,
  triggerOnlyProps,
  value: { date: value = "" },
  ...rest
}: DatePickerProps) {

  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const parsedDate = value ? new Date(value) : new Date();
  const [currentMonth, setCurrentMonth] = useState(parsedDate.getMonth());
  const [currentYear, setCurrentYear] = useState(parsedDate.getFullYear());
  const days = getDaysInMonth(currentMonth, currentYear);

  const isSmallScreen = window.innerWidth < 900;
  const closePicker = () => {
    (document.activeElement as HTMLElement | null)?.blur?.();
    setOpen(false);
  };

  useEffect(() => {
    return () => {
      (document.activeElement as HTMLElement | null)?.blur?.();
    };
  }, []);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const handleSelectDate = (date: Date) => {
    onChange?.({ date: date.toISOString(), error: false });
    closePicker();
  };
  const fieldId =
    InputFieldProps?.name ||
    InputFieldProps?.id ||
    `date-picker-${Math.random().toString(36).substr(2, 9)}`;

  const shouldShrink = labelShrink !== undefined ? labelShrink : undefined;

  const InputProps = {
    autoComplete: "off",
    id: fieldId,
    InputLabelProps: { shrink: shouldShrink },
    ...InputFieldProps,
    onMouseDown:
      triggerOnlyProps?.onMouseDown ||
      ((e: { preventDefault: () => void }) => e.preventDefault()),
    onClick:
      triggerOnlyProps?.onClick ||
      (() => {
        if (!disableInternalOpen) {
          setOpen((p) => !p);
        }
      }),
    onFocusCapture: triggerOnlyProps?.onFocusCapture,
    onKeyDown:
      triggerOnlyProps?.onKeyDown ||
      ((e: any) => {
        if (!editable) e.preventDefault();
      }),
    error: InputFieldProps?.helperText ? InputFieldProps?.error : false,
    value: value ? parsedDate.toLocaleDateString() : "",
    placeholder: t("select_date"),
    readOnly: true,
    inputProps: {
      ...InputFieldProps?.inputProps,
      ...triggerOnlyProps?.inputProps,
      readOnly: true,
      inputMode: "none" as const,
    },
    customLabelStyles: customLabelStyles,
  };

  // Create padded days: add empty slots before the 1st of the month
  const firstDayOfWeek = new Date(currentYear, currentMonth, 1).getDay();
  const paddedDays = [...Array(firstDayOfWeek).fill(null), ...days];

  const CalendarContent = () => (
    <Box
      bgcolor="#ffffff"
      boxShadow={0}
      style={{ zIndex: 1500 }}
      borderRadius={"12px"}
      p={2}
      sx={{
        width: isSmallScreen ? "100%" : "300px",
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        {/* Prev month button */}
        <IconButton
          size="small"
          disabled={
            currentYear < new Date().getFullYear() ||
            (currentYear === new Date().getFullYear() &&
              currentMonth <= new Date().getMonth())
          }
          onClick={() => {
            setCurrentMonth((prev) => {
              if (prev === 0) {
                setCurrentYear((y) => y - 1);
                return 11;
              }
              return prev - 1;
            });
          }}
          sx={{
            color: "black",
            border: "1px solid #ECEBF2",
            borderRadius: "8px",
            padding: "7px",
            "&:hover": {
              backgroundColor: "#F9F9F9",
            },
          }}
        >
          {i18n.language === "en" ? (
            <ArrowBackIosNewIcon fontSize="small" />
          ) : (
            <ArrowForwardIosIcon fontSize="small" />
          )}
        </IconButton>
        <Typography fontWeight={700} fontSize={16}>
          {new Date(currentYear, currentMonth).toLocaleString(i18n.language, {
            month: "long",
            year: "numeric",
          })}
        </Typography>
        {/* Next month button */}
        <IconButton
          size="small"
          onClick={() => {
            setCurrentMonth((prev) => {
              if (prev === 11) {
                setCurrentYear((y) => y + 1);
                return 0;
              }
              return prev + 1;
            });
          }}
          sx={{
            color: "black",
            border: "1px solid #ECEBF2",
            borderRadius: "8px",
            padding: "7px",
            "&:hover": {
              backgroundColor: "#F9F9F9",
            },
          }}
        >
          {i18n.language === "en" ? (
            <ArrowForwardIosIcon fontSize="small" />
          ) : (
            <ArrowBackIosNewIcon fontSize="small" />
          )}
        </IconButton>
      </Grid>

      {/* Days of week - Using CSS Grid instead of MUI Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "8px",
          mb: 1,
        }}
      >
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <Box
            key={d}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 0, // Important for proper grid behavior
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              align="center"
              sx={{ color: "#666" }}
            >
              {d}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Calendar grid - Using CSS Grid for perfect alignment */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "8px",
          mt: 1,
        }}
      >
        {paddedDays.map((day, index) => {
          if (!day) {
            return <Box key={`empty-${index}`} />;
          }

          const isSelected =
            parsedDate && day.toDateString() === parsedDate.toDateString();
          const isToday = day.getTime() === today.getTime();
          const isPast = day < today;

          return (
            <Box
              key={day.toISOString()}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 0, // Important for proper grid behavior
              }}
            >
              <IconButton
                disabled={isPast}
                onClick={() => handleSelectDate(day)}
                sx={{
                  width: "100%",
                  maxWidth: 42,
                  height: 42,
                  borderRadius: "8px !important",
                  border: isToday ? "1px solid #520A76" : "none",
                  backgroundColor: isSelected ? "#520A76" : "transparent",
                  "&:hover": {
                    backgroundColor: isSelected
                      ? "#520A76"
                      : isPast
                      ? "transparent"
                      : "#F9F9F9",
                  },
                  "&:disabled": {
                    backgroundColor: "transparent",
                    cursor: "not-allowed",
                  },
                  "& .MuiTypography-root": {
                    color: isSelected
                      ? "#fff"
                      : isPast
                      ? "#00000066"
                      : "#0A0A0E",
                    fontWeight: 700,
                    fontSize: "14px",
                  },
                  "&:disabled .MuiTypography-root": {
                    color: "#00000066 !important",
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    fontSize: "14px",
                  }}
                >
                  {day.getDate()}
                </Typography>
              </IconButton>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  if (renderPickerOnly) {
    return <CalendarContent />;
  }

  return (
    <>
      <Input
        StartIcon={StartIcon}
        {...InputProps}
        customLabelStyles={customLabelStyles}
      />

      {open &&
        (isSmallScreen ? (
          <DrawerComponent open={open} onClose={closePicker}>
            <CalendarContent />
          </DrawerComponent>
        ) : (
          <Popper
            open={open}
            anchorEl={document.getElementById(fieldId)}
            placement="bottom-start"
            sx={{ zIndex: 1500, mt: 1 }}
          >
            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <Paper
                elevation={3}
                sx={{
                  borderRadius: 3,
                  mt: 2,
                  boxShadow: "0px 8px 30px rgba(0,0,0,0.12)",
                }}
              >
                <CalendarContent />
              </Paper>
            </ClickAwayListener>
          </Popper>
        ))}
    </>
  );
}

const DrawerComponent = ({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  const { t, i18n } = useTranslation();
  return (
    <Drawer
      anchor="bottom"
      open={true}
      onClose={onClose}
      ModalProps={{
        disableAutoFocus: true,
        disableEnforceFocus: true,
        disableRestoreFocus: true,
      }}
      PaperProps={{
        style: {
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          padding: "16px",
          minHeight: "50vh",
          overflowX: "hidden",
        },
      }}
      sx={{ zIndex: "1400 !important" }}
    >
      <Box
        component={"img"}
        src={CloseIcon}
        alt="X"
        top={"16px"}
        position={"absolute"}
        onClick={() => onClose()}
        right={i18n.language === "ar" ? "auto" : "16px"}
        left={i18n.language === "ar" ? "16px" : "auto"}
        sx={{ cursor: "pointer" }}
      />
      <Box sx={{ textAlign: "center", mb: 2 }}>
        <h3 style={{ margin: 0, fontSize: "16px" }}>{t("date")}</h3>
      </Box>
      {children}
    </Drawer>
  );
};
